<template>
    <div>
        <!-- LOADING -->
        <loading-component v-if="!dashboards"> </loading-component>
        <!-- DASHBOARD -->
        <div v-else-if="activeDashboard">
            <div class="flex-container flex-align-center">
                <navigation
                    :entity="entity"
                    :dashboards="dashboards"
                    :dashboard="activeDashboard"
                    @navigate="handleNavigate"
                ></navigation>
                <a class="ml-3" @click.prevent="handleNavigate(-1)">View All</a>
                <dashboard-create
                    v-can.hide="$can('dashboards:create')"
                    :dashboard-type-id="dashboardTypeId"
                    @add="handleAddDashboard"
                >
                    <a class="ml-3" slot="trigger" type="text"
                        ><Icon custom="icon icon-add"></Icon>
                        New Dashboard
                    </a>
                </dashboard-create>
                <div class="flex-item-pull-right">
                    <confirm-modal
                        v-if="
                            $can('dashboards:delete') ||
                            activeDashboard.user_id == $store.getters['me/ID']
                        "
                        :visible="showDelete"
                        :title="'Delete this dashboard?'"
                        :information="'You cannot undo this step.'"
                        :buttonLabel="'Yes, Delete'"
                        @confirm="handleDelete(activeDashboard.id)"
                        @cancel="showDelete = false"
                    >
                        <Button
                            custom-icon="icon icon-bin"
                            class="text-danger"
                            slot="trigger"
                            @click.stop.prevent="showDelete = true"
                        >
                        </Button>
                    </confirm-modal>
                    <dashboard-update
                        v-if="
                            $can('dashboards:update') ||
                            activeDashboard.user_id == $store.getters['me/ID']
                        "
                        :dashboard="activeDashboard"
                        @update="handleUpdate"
                    >
                        <Button
                            slot="trigger"
                            custom-icon="icon icon-pencil-1"
                        ></Button
                    ></dashboard-update>
                </div>
            </div>
            <dashboard
                class="clear-both"
                :entity="entity"
                :dashboard-id="activeDashboard.id"
            ></dashboard>
        </div>
        <!-- LIST -->
        <div v-else-if="dashboards.length > 0">
            <div v-if="owned.length > 0">
                <div class="flex-container flex-align-center">
                    <h2 class="font-weight-normal">My Dashboards</h2>
                    <div class="flex-item-pull-right">
                        <dashboard-create
                            @add="handleAddDashboard"
                            :dashboard-type-id="dashboardTypeId"
                        >
                            <Button
                                class="text-primary"
                                slot="trigger"
                                custom-icon="icon icon-add"
                                >Add a Dashboard</Button
                            >
                        </dashboard-create>
                    </div>
                </div>
                <dashboard-list
                    class="mt-5"
                    :dashboards="owned"
                    :entity="entity"
                    @navigate="handleNavigate"
                >
                </dashboard-list>
            </div>
            <empty-state v-else class="text-center mt-8">
                <empty-state-illustration
                    slot="illustration"
                    class="util-max-w200"
                ></empty-state-illustration>
                <p slot="text">You haven't created any dashboards yet.</p>
                <div slot="actions">
                    <dashboard-create
                        @add="handleAddDashboard"
                        :dashboard-type-id="dashboardTypeId"
                    >
                        <Button
                            slot="trigger"
                            custom-icon="icon icon-add"
                            type="primary"
                            >Create your first Dashboard</Button
                        >
                    </dashboard-create>
                </div>
            </empty-state>

            <div class="mt-5" v-if="shared.length > 0">
                <Divider></Divider>
                <h2 class="font-weight-normal">Shared Dashboards</h2>
                <dashboard-list
                    class="mt-5"
                    :entity="entity"
                    :dashboards="shared"
                    @navigate="handleNavigate"
                ></dashboard-list>
            </div>
        </div>
        <!-- EMPTY STATE -->
        <empty-state v-else class="text-center mt-8">
            <empty-state-illustration
                slot="illustration"
                class="util-max-w200"
            ></empty-state-illustration>
            <p slot="text">You don't have any dashboards.</p>
            <div slot="actions">
                <dashboard-create
                    @add="handleAddDashboard"
                    :dashboard-type-id="dashboardTypeId"
                >
                    <Button
                        class="mb-5"
                        slot="trigger"
                        custom-icon="icon icon-add"
                        type="primary"
                        >Add a Dashboard</Button
                    >
                </dashboard-create>
            </div>
        </empty-state>
    </div>
</template>
<script>
export default {
    props: {
        entity: Object,
        params: Array,
        meDashboardsRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/me-dashboards.js"))();
            },
        },
        dashboardsRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/base.js"))(
                    window.akita.urls.api + "/v1/dashboards"
                );
            },
        },
    },
    data() {
        return {
            localStorageKey: "dashboards_default_" + this.entity.type_id,
            dashboards: null,
            dashboard: null,
            dashboardId: null,
            showDelete: false,
        };
    },
    components: {
        Dashboard: function () {
            return import("./dashboard");
        },
        Navigation: function () {
            return import("./navigation");
        },
        DashboardList: function () {
            return import("./list");
        },
        DashboardCreate: function () {
            return import("@/js/components/partials/dashboard-create");
        },
        DashboardUpdate: function () {
            return import("@/js/components/partials/dashboard-update");
        },
        EmptyState: function () {
            return import("@/js/components/partials/empty-state");
        },
        EmptyStateIllustration: function () {
            return import("@/js/components/partials/illustrations/dashboard");
        },
        ConfirmModal: function () {
            return import("@/js/components/partials/confirm-modal");
        },
    },
    created() {
        this.init();
    },
    computed: {
        dashboardTypeId() {
            return this.entity.type_id == 1 ? 2 : 3;
        },
        activeDashboard() {
            /**
             * Is there one in the route
             */
            if (!this.dashboardId) {
                if (_.get(this.params, 0)) {
                    this.dashboardId = _.get(this.params, 0);
                }
            }

            /**
             * Is there a dashboard id in localstorage
             */
            if (!this.dashboardId) {
                this.dashboardId = this.$localStorageCache.get(
                    this.localStorageKey
                );
            }

            /**
             * Does this id exist
             */
            let dashboard = _.find(this.dashboards, {
                id: this.dashboardId,
            });
            if (dashboard) {
                return dashboard;
            }
        },
        owned() {
            return _.chain(this.dashboards || [])
                .filter((dashboard) => {
                    return dashboard.user_id == this.$store.getters["me/ID"];
                })
                .orderBy(["name"], ["asc"])
                .value();
        },
        shared() {
            return _.chain(this.dashboards || [])
                .filter((dashboard) => {
                    return dashboard.user_id != this.$store.getters["me/ID"];
                })
                .orderBy(["name"], ["asc"])
                .value();
        },
    },
    methods: {
        async init() {
            /**
             * Get the available dashboards based on the entity type
             */
            await this.queryDashboards();
        },
        async queryDashboards() {
            let request = {
                params: {
                    q: ["version[eq]=2", "type_id[eq]=" + this.dashboardTypeId],
                    sort_by: "sort_order:asc,name:asc",
                    include: [],
                },
            };
            const response = await this.meDashboardsRepository.index(request);
            this.dashboards = response.data.data;
        },
        handleNavigate(dashboardId) {
            if (dashboardId < 1) {
                this.$localStorageCache.set(this.localStorageKey, null);
                this.dashboardId = -1;
                return;
            }
            this.$localStorageCache.set(this.localStorageKey, dashboardId);
            this.dashboardId = dashboardId;
        },
        async handleAddDashboard(payload) {
            let response = await this.dashboardsRepository.store(payload);
            let dashboard = response.data.data;
            this.$Message.success({
                content: "New dashboard added.",
            });
            this.dashboards.push(dashboard);
            this.dashboardId = dashboard.id;
        },
        async handleDelete(id) {
            this.showDelete = false;
            await this.dashboardsRepository.remove(id);
            var index = this.dashboards
                .map(function (item) {
                    return item.id;
                })
                .indexOf(id);
            if (index > -1) {
                this.dashboards.splice(index, 1);
            }
            this.dashboardId = null;
        },
        async handleUpdate(dashboard) {
            var response = await this.dashboardsRepository.update(
                dashboard.id,
                dashboard
            );
            this.$Message.success({
                content: "Dashboard updated.",
            });
            var index = this.dashboards
                .map(function (item) {
                    return item.id;
                })
                .indexOf(dashboard.id);
            if (index > -1) {
                this.dashboards.splice(index, 1, dashboard);
            }
        },
    },
};
</script>
